import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Star, Pencil, Trash2, Plus } from 'lucide-react';

export interface TemplateOption {
  id: string;
  name: string;
  prompt: string;
  is_system?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface TemplateSelectProps {
  options: TemplateOption[];
  value: string;
  onChange: (value: string) => void;
  onAdd?: () => void;
  onEdit?: (template: TemplateOption) => void;
  onDelete?: (template: TemplateOption) => void;
  className?: string;
  disabled?: boolean;
}

interface DropdownPosition {
  top: number | 'auto';
  bottom: number | 'auto';
  left: number;
  maxHeight: number;
}

const DROPDOWN_MIN_WIDTH = 220;
const DROPDOWN_MAX_WIDTH = 280;
const VIEWPORT_PADDING = 8;

export function TemplateSelect({
  options,
  value,
  onChange,
  onAdd,
  onEdit,
  onDelete,
  className = '',
  disabled = false,
}: TemplateSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.id === value);

  // Calculate position based on container element
  const calculatePosition = useCallback((): DropdownPosition | null => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space
    const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;

    // Determine if we should open upward
    const preferredHeight = 300;
    const openUpward = spaceBelow < preferredHeight && spaceAbove > spaceBelow;

    // Calculate max height based on available space
    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(Math.max(availableSpace - 8, 120), 300);

    // Calculate left position, ensuring dropdown doesn't overflow right edge
    let left = rect.left;
    const dropdownWidth = Math.min(DROPDOWN_MAX_WIDTH, Math.max(DROPDOWN_MIN_WIDTH, rect.width));
    if (left + dropdownWidth > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - dropdownWidth - VIEWPORT_PADDING;
    }
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    return {
      top: openUpward ? 'auto' : rect.bottom + 4,
      bottom: openUpward ? viewportHeight - rect.top + 4 : 'auto',
      left,
      maxHeight,
    };
  }, []);

  // Handle opening the dropdown
  const handleOpen = useCallback(() => {
    const pos = calculatePosition();
    if (pos) {
      setPosition(pos);
      setIsOpen(true);
    }
  }, [calculatePosition]);

  // Handle toggle
  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      handleOpen();
    }
  }, [isOpen, handleOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update position on scroll or resize while open
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const pos = calculatePosition();
      if (pos) {
        setPosition(pos);
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, calculatePosition]);

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
  };

  const handleEdit = (e: React.MouseEvent, option: TemplateOption) => {
    e.stopPropagation();
    setIsOpen(false);
    onEdit?.(option);
  };

  const handleDelete = (e: React.MouseEvent, option: TemplateOption) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete?.(option);
  };

  const handleAdd = () => {
    setIsOpen(false);
    onAdd?.();
  };

  const dropdownContent = isOpen && position && (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: position.top === 'auto' ? undefined : position.top,
        bottom: position.bottom === 'auto' ? undefined : position.bottom,
        left: position.left,
        minWidth: DROPDOWN_MIN_WIDTH,
        maxWidth: DROPDOWN_MAX_WIDTH,
      }}
      className="z-[9999] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-800"
    >
      <div
        className="overflow-y-auto p-1"
        style={{ maxHeight: position.maxHeight }}
      >
        {options.map((option) => (
          <div
            key={option.id}
            className={`group flex w-full items-center gap-1 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-50 dark:hover:bg-zinc-700 ${
              value === option.id
                ? 'bg-indigo-50 dark:bg-indigo-900/30'
                : ''
            }`}
          >
            <button
              type="button"
              onClick={() => handleSelect(option.id)}
              className={`flex flex-1 items-center gap-2 text-left text-xs ${
                value === option.id
                  ? 'text-indigo-700 font-medium dark:text-indigo-400'
                  : 'text-slate-700 dark:text-zinc-200'
              }`}
            >
              {option.is_system ? (
                <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
              ) : (
                <span className="w-3 shrink-0" />
              )}
              <span className="truncate flex-1">{option.name}</span>
              {value === option.id && (
                <Check className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
              )}
            </button>
            {!option.is_system && (onEdit || onDelete) && (
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {onEdit && (
                  <button
                    type="button"
                    onClick={(e) => handleEdit(e, option)}
                    className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 dark:hover:bg-zinc-600 dark:text-zinc-400 dark:hover:text-zinc-200"
                    title="Edit template"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, option)}
                    className="p-1 rounded hover:bg-rose-100 text-slate-400 hover:text-rose-600 dark:hover:bg-rose-900/30 dark:text-zinc-400 dark:hover:text-rose-400"
                    title="Delete template"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {onAdd && (
        <>
          <div className="border-t border-slate-100 dark:border-zinc-700" />
          <div className="p-1">
            <button
              type="button"
              onClick={handleAdd}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-slate-600 transition-colors hover:bg-slate-50 dark:text-zinc-300 dark:hover:bg-zinc-700"
            >
              <Plus className="h-3 w-3 shrink-0" />
              <span>New template</span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={`flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white pl-2.5 pr-2 text-xs text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 ${
          isOpen ? 'ring-2 ring-indigo-500' : ''
        }`}
      >
        {selectedOption?.is_system && (
          <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
        )}
        <span className="truncate max-w-[120px]">
          {selectedOption?.name || 'Select template'}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 shrink-0 dark:text-zinc-400 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {createPortal(dropdownContent, document.body)}
    </div>
  );
}
